// ============================================================
// DEALIN MVP - Main App Component
// ============================================================

import { QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { queryClient } from '@/lib/queryClient';
import { AppRouter } from '@/routes';
import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { initGA } from '@/components/analytics/GoogleAnalytics';
import { initPostHog, identifyUser, resetUser } from '@/lib/posthog';

function App() {
  const initialize = useAuthStore((state) => state.initialize);
  const user = useAuthStore((state) => state.user);
  const profile = useAuthStore((state) => state.profile);
  const vendor = useAuthStore((state) => state.vendor);
  const isVendor = useAuthStore((state) => state.isVendor);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const prevUserId = useRef<number | null>(null);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    initGA();
    initPostHog();
  }, []);

  // PostHog: identify user on auth state change
  useEffect(() => {
    if (isAuthenticated && user && user.id !== prevUserId.current) {
      identifyUser(user.id, {
        email: user.email,
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        username: user.username,
        role: isVendor ? 'vendor' : 'client',
        company_name: vendor?.name || profile?.company_name || '',
        vendor_id: vendor?.id || null,
      });
      prevUserId.current = user.id;
    } else if (!isAuthenticated && prevUserId.current !== null) {
      resetUser();
      prevUserId.current = null;
    }
  }, [isAuthenticated, user, profile, vendor, isVendor]);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AppRouter />
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
